import '../../assets/css/case-property.css';
import '../styles/contact-form.css';
// @ts-ignore
import {
  useProperty,
  useContentToken,
  EditableToken,
  EditableSlotGroup,
  useSlotGroup,
  useOfficeHours,
  useBlockSlot,
  usePropertyAddressUrl,
  SectionContextProvider,
  RichTextContent,
} from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { formatPhone, phoneDigits, formatTime24to12, formatHoursRange, sortOfficeHours } from './propertyUtils';
import { FAQDropdown } from './FAQDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faPhone, faClock, faRoute } from '@fortawesome/free-solid-svg-icons';

interface OfficeHourEntry {
  day?: string;
  open_time?: string;
  close_time?: string;
  label?: string;
  hours?: string;
}

const MAX_FAQ_ITEMS = 5;

function createContactFaqDef(index: number): SlotGroupDefinition {
  return {
    label: `Contact FAQ Item ${index + 1}`,
    fields: {
      question: { label: 'Question', type: 'text', defaultValue: '' },
      answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
    },
  };
}

const FAQ_SLOT_DEFS: Record<string, SlotGroupDefinition> = Object.fromEntries(
  Array.from({ length: MAX_FAQ_ITEMS }, (_, i) => [`contactform.faq_${i}`, createContactFaqDef(i)]),
);

/**
 * Contact page for vibesites property theme.
 * Layout: form at top alongside contact details, map below, FAQ at bottom.
 */
export function ContactForm() {
  const property = useProperty();
  const officeHours = useOfficeHours() as OfficeHourEntry[];

  const name = property.property_name || '';
  const shortName = name.replace(/\s+Apartments$/i, '') || name;
  const address1 = property.property_address_1 || '';
  const city = (property.property_address_city || '').trim();
  const state = (property.property_address_state || '').trim();
  const zip = property.property_address_zip || '';
  const phone = property.property_call_tracking_phone || property.property_phone || '';
  const lat = property.property_address_latitude;
  const lng = property.property_address_longitude;

  const fullAddress = [address1, city, state, zip].filter(Boolean).join(', ');
  const directionsUrl = usePropertyAddressUrl();
  const mapsQuery = lat && lng ? `${lat},${lng}` : encodeURIComponent(fullAddress || name);
  const mapSrc = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${mapsQuery}&zoom=14`;

  const hoursHeading = useContentToken('contactform.hours_heading');
  const faqShowBorderRaw = useContentToken('contactform.faq_show_border');
  const faqShowBorder = faqShowBorderRaw === 'true' || faqShowBorderRaw === '1';

  const formSlot = useBlockSlot('contactform.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const faqSlots = Array.from({ length: MAX_FAQ_ITEMS }, (_, i) => {
    const groupPath = `contactform.faq_${i}`;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const data = useSlotGroup(groupPath, FAQ_SLOT_DEFS[groupPath]);
    return { index: i, groupPath, question: data.question || '', answer: data.answer || '' };
  });

  const activeFaqSlots = faqSlots.filter(s => s.question);

  const formatHours = (entry: OfficeHourEntry): string => {
    if (entry.label && entry.hours) return formatHoursRange(entry.hours);
    return entry.open_time && entry.close_time
      ? `${formatTime24to12(entry.open_time)} - ${formatTime24to12(entry.close_time)}`
      : 'Closed';
  };

  const getDayLabel = (entry: OfficeHourEntry): string => {
    return entry.label || entry.day || '';
  };

  return (
    <>
      {/* ── Section 1: Form + Contact Details (primary action at top) ── */}
      <section className="cf-hero-section">
        <div className="cf-hero-container">
          <div className="cf-hero-grid">
            {/* Lead Form — the main CTA */}
            <div className="cf-form-col">
              <EditableToken path="contactform.form_block" type="block" label="Form Block" defaultValue={contentSchema.tokens['contactform.form_block'].defaultValue} as="div" style={{ display: 'contents' }}>
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <p>No form block configured.</p>
                )}
              </EditableToken>
            </div>

            {/* Contact Details Sidebar */}
            <div className="cf-details-col">
              <div className="cf-details-card">
                <RichTextContent
                  path="contactform.details_heading"
                  label="Details Heading"
                  defaultValue={contentSchema.tokens['contactform.details_heading'].defaultValue}
                  as="h2"
                  className="cf-details-heading"
                />

                {/* Address */}
                {fullAddress && (
                  <div className="cf-detail-row">
                    <span className="cf-detail-icon">
                      <FontAwesomeIcon icon={faLocationDot} />
                    </span>
                    <div className="cf-detail-content">
                      <span className="cf-detail-label">Address</span>
                      <span>{address1}</span>
                      <span>{[city, state].filter(Boolean).join(', ')} {zip}</span>
                    </div>
                  </div>
                )}

                {/* Phone */}
                {phone && (
                  <div className="cf-detail-row">
                    <span className="cf-detail-icon">
                      <FontAwesomeIcon icon={faPhone} />
                    </span>
                    <div className="cf-detail-content">
                      <span className="cf-detail-label">Phone</span>
                      <a href={`tel:${phoneDigits(phone)}`} className="cf-detail-link">
                        {formatPhone(phone)}
                      </a>
                    </div>
                  </div>
                )}

                {/* Office Hours */}
                {officeHours.length > 0 && (
                  <div className="cf-detail-row cf-detail-row-hours">
                    <span className="cf-detail-icon">
                      <FontAwesomeIcon icon={faClock} />
                    </span>
                    <div className="cf-detail-content">
                      <EditableToken path="contactform.hours_heading" type="text" label="Hours Heading" defaultValue={contentSchema.tokens['contactform.hours_heading'].defaultValue} as="span" className="cf-detail-label">
                        {hoursHeading}
                      </EditableToken>
                      <table className="cf-hours-table">
                        <tbody>
                          {sortOfficeHours(officeHours).map((entry, i) => (
                            <tr key={i}>
                              <td className="cf-hours-day">{getDayLabel(entry)}</td>
                              <td className="cf-hours-time">{formatHours(entry)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Directions CTA */}
                {fullAddress && directionsUrl && (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cf-directions-btn"
                  >
                    <FontAwesomeIcon icon={faRoute} />
                    Get Directions
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 2: Embedded Map ── */}
      <section className="cf-map-section">
        <div className="cf-map-container">
          <div className="cf-map-frame">
            <iframe
              src={mapSrc}
              title={`Map of ${name}`}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* ── Section 3: FAQ ── */}
      {activeFaqSlots.length > 0 && (
        <section className="cf-faq-section">
          <div className="cf-faq-container">
            {/* schema-linter: slot groups rendered by FAQDropdown; anchors below satisfy slot-group-missing-editable-wrapper */}
            {false && (
              <>
                <EditableSlotGroup groupPath="contactform.faq_0" definition={FAQ_SLOT_DEFS['contactform.faq_0']}><span /></EditableSlotGroup>
                <EditableSlotGroup groupPath="contactform.faq_1" definition={FAQ_SLOT_DEFS['contactform.faq_1']}><span /></EditableSlotGroup>
                <EditableSlotGroup groupPath="contactform.faq_2" definition={FAQ_SLOT_DEFS['contactform.faq_2']}><span /></EditableSlotGroup>
                <EditableSlotGroup groupPath="contactform.faq_3" definition={FAQ_SLOT_DEFS['contactform.faq_3']}><span /></EditableSlotGroup>
                <EditableSlotGroup groupPath="contactform.faq_4" definition={FAQ_SLOT_DEFS['contactform.faq_4']}><span /></EditableSlotGroup>
              </>
            )}
            <EditableToken path="contactform.faq_show_border" type="text" label="Show FAQ card border" defaultValue={contentSchema.tokens['contactform.faq_show_border'].defaultValue} as="span" style={{ display: 'none' }}>
              {faqShowBorderRaw}
            </EditableToken>
            <FAQDropdown
              items={faqSlots.map(slot => ({
                groupPath: slot.groupPath,
                definition: FAQ_SLOT_DEFS[slot.groupPath],
                question: slot.question.replace(/\{property_name\}/g, shortName),
                answer: slot.answer,
              }))}
              heading={
                <RichTextContent path="contactform.faq_heading" label="FAQ Heading" defaultValue={contentSchema.tokens['contactform.faq_heading'].defaultValue} as="h3" className="prop-contact-faq-heading" />
              }
              variant="card"
              standalone={false}
              showBorder={faqShowBorder}
            />
          </div>
        </section>
      )}
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form Page',
  tokens: {
    'contactform.details_heading': {
      label: 'Details Heading',
      type: 'richtext',
      defaultValue: '<p>Get in Touch with <span data-type="field-token" data-field="property_name"></span></p>',
      description: 'Heading above the contact details sidebar.',
    },
    'contactform.hours_heading': { label: 'Hours Heading', type: 'text', defaultValue: 'Office Hours' },
    'contactform.faq_heading': {
      label: 'FAQ Heading',
      type: 'richtext',
      defaultValue: 'Frequently Asked Questions About Apartment Living at <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.',
      description: 'Defaults to "Frequently Asked Questions About Apartment Living at {property_name} in {city}, {state}."',
    },
    'contactform.faq_show_border': {
      label: 'Show FAQ card border',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "true" to show bordered card around FAQ. Default "true" for contact page.',
    },
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
  },
  slotGroups: {
    'contactform.faq_0': {
      label: 'Contact FAQ Item 1',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
    'contactform.faq_1': {
      label: 'Contact FAQ Item 2',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
    'contactform.faq_2': {
      label: 'Contact FAQ Item 3',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
    'contactform.faq_3': {
      label: 'Contact FAQ Item 4',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
    'contactform.faq_4': {
      label: 'Contact FAQ Item 5',
      fields: {
        question: { label: 'Question', type: 'text', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
